/**
 * API Error Handler
 * 
 * Centralized error handling for API calls that automatically handles
 * session expiration and authentication failures.
 * 
 * Features:
 * - Detects 401/403 errors (session expired)  
 * - Automatically logs out user and redirects to login
 * - Prevents multiple redirects with a guard flag
 * - Clears all auth-related storage and cookies
 */

import { useAuthStore } from '@/lib/stores/auth-store'

// Guard to prevent multiple simultaneous logouts
let isLoggingOut = false

/**
 * Check if an error is an authentication error (401/403)
 */
export function isAuthError(error: any): boolean {
    if (!error) return false

    // Check HTTP status codes
    if (error.status === 401 || error.status === 403) {
        return true
    }

    // Check Response object
    if (error instanceof Response && (error.status === 401 || error.status === 403)) {
        return true
    }

    // Check error message patterns
    const errorMessage = error.message || error.toString()
    const authPatterns = [
        /401/i,
        /403/i,
        /unauthorized/i,
        /unauthenticated/i,
        /authentication.*failed/i,
        /session.*expired/i,
        /token.*expired/i,
        /token.*invalid/i,
        /no.*valid.*session/i,
    ]

    return authPatterns.some(pattern => pattern.test(errorMessage))
}

/**
 * Handle authentication errors by logging out and redirecting to login
 */
export function handleAuthError(error?: any): void {
    // Prevent multiple simultaneous logout attempts
    if (isLoggingOut) {
        console.log('[AuthErrorHandler] Logout already in progress, skipping')
        return
    }

    console.error('[AuthErrorHandler] Authentication error detected:', {
        status: error?.status,
        message: error?.message || error,
    })

    isLoggingOut = true

    try {
        // Clear all auth-related data
        if (typeof window !== 'undefined') {
            // Clear localStorage
            localStorage.removeItem('oxigen_auth_session')

            // Clear JWT token cookie
            document.cookie = 'jwt-token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT'

            // Clear auth store
            const authStore = useAuthStore.getState()
            authStore.logout()

            console.log('[AuthErrorHandler] Session cleared, redirecting to login')

            // Redirect to login page with return URL
            const returnUrl = encodeURIComponent(window.location.pathname + window.location.search)
            window.location.href = `/login?returnUrl=${returnUrl}`
        }
    } catch (logoutError) {
        console.error('[AuthErrorHandler] Error during logout:', logoutError)

        // Force redirect anyway
        if (typeof window !== 'undefined') {
            window.location.href = '/login'
        }
    } finally {
        // Reset guard after a delay to allow navigation
        setTimeout(() => {
            isLoggingOut = false
        }, 1000)
    }
}

/**
 * Wrapper around fetch that automatically handles authentication errors
 * Use this instead of raw fetch for API calls that require authentication
 * 
 * @param input - URL or Request object
 * @param init - RequestInit options
 * @returns Promise<Response>
 * 
 * @example
 * const response = await fetchWithAuthHandler('/api/sprints', { method: 'GET' })
 */
export async function fetchWithAuthHandler(
    input: RequestInfo | URL,
    init?: RequestInit
): Promise<Response> {
    try {
        const response = await fetch(input, init)

        // Check for authentication errors
        if (response.status === 401 || response.status === 403) {
            console.warn('[FetchWithAuthHandler] Auth error detected on request:', {
                url: typeof input === 'string' ? input : input instanceof Request ? input.url : String(input),
                status: response.status,
            })

            handleAuthError({ status: response.status })

            // Throw error to prevent further processing
            throw new Error(`Authentication failed: ${response.status}`)
        }

        return response
    } catch (error) {
        // Check if the error is an auth error
        if (isAuthError(error)) {
            handleAuthError(error)
        }

        // Re-throw the error for the caller to handle
        throw error
    }
}

/**
 * Handle errors from API responses with automatic auth error detection
 * Use this in catch blocks to ensure auth errors trigger logout
 * 
 * @param error - The error to handle
 * @param fallback - Optional fallback handler for non-auth errors
 * 
 * @example
 * try {
 *   const response = await fetch('/api/sprints')
 *   // ...
 * } catch (error) {
 *   handleApiError(error, (err) => {
 *     // Handle other errors
 *     toast.error('Failed to load data')
 *   })
 * }
 */
export function handleApiError(
    error: any,
    fallback?: (error: any) => void
): void {
    // Check if it's an auth error first
    if (isAuthError(error)) {
        handleAuthError(error)
        return
    }

    // Otherwise, use the fallback handler if provided
    if (fallback) {
        fallback(error)
    }
}
